import { FC } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Image } from '@/atoms/Image'
import { AsDiv, CaptionSM, EyebrowMD, EyebrowSM, HeadingLG, HeadingXS, TitleMD } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { Modal } from '@/molecules/Modal'
import { ValidEarlyBirdDiscount } from '@/services/Theatrical/queries'
import { useTranslate } from '@/utils/translate/translate-client'
import { FlexibleBookingToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/SpecificTimesToolTip'
import TimeLeft from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/TimeLeft'
import { PromoImage } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { useRestrictionCopy } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'
import useCopyCode from '../../hooks/useCopyCode'

interface Props {
  onClose: () => void
  open: boolean
  backgroundImage: PromoImage
  accentColor: string
  earlyBirdDiscount: ValidEarlyBirdDiscount
  openingWeek: string
  code: string
  backgroundIsDark: boolean
  theatricalSlug: string
}

const EarlyBirdModal: FC<Props> = ({
  open,
  onClose,
  backgroundImage,
  accentColor,
  earlyBirdDiscount,
  openingWeek,
  code,
  backgroundIsDark,
  theatricalSlug,
}) => {
  const { t } = useTranslate('tickets')
  const restrictionCopy = useRestrictionCopy(earlyBirdDiscount, openingWeek)
  const { copiedCode, handleCopyCode } = useCopyCode(code)
  const textColor = backgroundIsDark ? 'core-gray-100' : 'core-gray-950'
  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      panelClasses="!bg-core-gray-950 !w-[90%] !max-w-[380px] shadow-dark-4 rounded-3xl relative text-center"
      transition="appear"
      className="z-[99999]"
    >
      <Image src={backgroundImage.url} fill className="absolute object-center" alt="" quality={50} />
      <div className={textColor}>
        <div
          className={classNames(
            'absolute right-4 top-4 z-20 cursor-pointer rounded-full p-2',
            backgroundIsDark ? 'bg-white/10' : 'bg-black/10',
          )}
          onClick={onClose}
        >
          <CloseIcon color={backgroundIsDark ? 'white' : 'black'} size={18} />
        </div>
        <FlexColumn className="relative z-10 gap-6">
          <FlexColumn className="relative z-20 shrink-0 items-center gap-4">
            <HeadingXS weight="bold" color={textColor}>
              {theatricalSlug === slugs.animalFarm
                ? t('buyEarlySavePig', 'Buy Early. Save Pig.')
                : t('buyEarlySaveBig', 'Buy Early. Save Big.')}
            </HeadingXS>

            <div>
              <HeadingLG className="uppercase" weight="bold" color={textColor}>
                {t('percentOff', '{{percent}}% off', {
                  percent: Math.round(earlyBirdDiscount.activeChildDiscount.value * 100),
                })}
              </HeadingLG>
              <FlexColumn className="mt-3 items-center gap-0.5">
                <FlexColumn className="gap-1">
                  <EyebrowMD as={AsDiv} className="pl-2 text-[0.938rem] uppercase" color={textColor}>
                    {t('useCode', 'Use Code')}
                  </EyebrowMD>
                  <div className={classNames('relative flex items-center justify-center overflow-hidden')}>
                    <TitleMD
                      as={AsDiv}
                      weight="bold"
                      className="relative z-10 px-3 py-1 uppercase"
                      style={{ backgroundColor: accentColor, color: 'white' }}
                    >
                      {code}
                    </TitleMD>
                  </div>
                </FlexColumn>
              </FlexColumn>
            </div>
          </FlexColumn>

          <FlexColumn className="relative z-30 gap-1">
            {restrictionCopy && (
              <FlexRow className="mb-3 justify-center gap-1">
                <SpecificTimesToolTip textColor={textColor} buttonColor={textColor} copy={restrictionCopy}>
                  <TitleMD weight="bold">{t('onSelectShowtimes', 'On Select Showtimes')}</TitleMD>
                </SpecificTimesToolTip>
              </FlexRow>
            )}

            <FlexColumn className="relative mx-auto w-full max-w-[177px] rounded-2xl px-3 pb-0 pt-2">
              <EyebrowSM className="absolute -top-2 left-1/2 -translate-x-1/2 px-1 uppercase" color={textColor}>
                {t('endingIn', 'Ending In')}
              </EyebrowSM>
              <TimeLeft className="gap-4 border-white/30" />
            </FlexColumn>
            <FlexibleBookingToolTip
              className="mx-auto w-fit pt-2"
              panelPosition="CENTER"
              buttonColor={textColor}
              buttonClassName="backdrop-blur border !border-core-gray-800 w-full bg-none"
            />
          </FlexColumn>
          <FlexColumn className="items-center gap-3">
            <Button
              onClick={handleCopyCode}
              className="flex w-fit items-center gap-1"
              variant={backgroundIsDark ? 'white' : 'black'}
            >
              {copiedCode ? (
                <CheckIcon size={24} color={backgroundIsDark ? 'black' : 'white'} />
              ) : (
                <CopyIcon size={24} color={backgroundIsDark ? 'black' : 'white'} />
              )}
              {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
            </Button>
            <CaptionSM italic color={textColor}>
              {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
            </CaptionSM>
          </FlexColumn>
        </FlexColumn>
      </div>
    </Modal>
  )
}

export default EarlyBirdModal
